/**
 * AccessGuard — wrapper компонент для защиты контента по подписке
 * 
 * Usage:
 *   <AccessGuard featureId="monthly" fallback={<UpgradePrompt featureId="monthly" />}>
 *     <MonthlyForecast />
 *   </AccessGuard>
 * 
 * Логика:
 * - Если hasAccess → рендерит children
 * - Если нет → рендерит fallback (или дефолтный замок)
 */

import React from 'react';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { SubscriptionTier } from '../../../shared/types/access';

interface AccessGuardProps {
  featureId: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoading?: boolean;
  requiredTier?: SubscriptionTier;
}

const AccessGuard: React.FC<AccessGuardProps> = ({
  featureId,
  children,
  fallback,
  showLoading = true,
  requiredTier,
}) => {
  const { hasAccess, isLoading, error, userTier, daysLeft } = useSubscriptionAccess(featureId);

  // Показываем загрузку
  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-[#d4af37]">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-2">✨</div>
          <p className="text-sm">Проверка подписки...</p>
        </div>
      </div>
    );
  }

  // Ошибка — показываем дефолтный fallback
  if (error) {
    console.error('[AccessGuard] Error:', error);
    return fallback ? <>{fallback}</> : <DefaultLock requiredTier={requiredTier} featureId={featureId} />;
  }

  // Нет доступа
  if (!hasAccess) {
    return fallback ? <>{fallback}</> : <DefaultLock requiredTier={requiredTier} featureId={featureId} userTier={userTier} daysLeft={daysLeft} />;
  }

  // Доступ есть — рендерим children
  return <>{children}</>;
};

/**
 * Дефолтный компонент при отсутствии доступа
 */
const DefaultLock: React.FC<{
  requiredTier?: SubscriptionTier;
  featureId?: string;
  userTier?: string;
  daysLeft?: number;
}> = ({ requiredTier, featureId, userTier, daysLeft }) => {
  const tierLabel = requiredTier
    ? { free: 'Free', base: 'Base', premium: 'Premium', pro: 'Pro' }[requiredTier]
    : 'Подписка';

  const tierColor = requiredTier
    ? { free: '#9ca3af', base: '#4fd1c5', premium: '#d4af37', pro: '#9B59B6' }[requiredTier]
    : '#9ca3af';

  return (
    <div
      className="max-w-[500px] mx-auto my-12 p-8 text-center rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, rgba(13, 25, 48, 0.8) 0%, rgba(30, 58, 95, 0.6) 100%)',
        backdropFilter: 'blur(20px)',
        border: `1px solid ${tierColor}40`,
        boxShadow: `0 8px 32px ${tierColor}20`,
      }}
    >
      <div className="text-5xl mb-4">🔒</div>
      <h3 className="text-xl font-semibold mb-2" style={{ color: tierColor }}>
        Требуется подписка {tierLabel}
      </h3>
      <p className="text-gray-400 mb-6">
        {featureId ? `Функция "${featureId}" доступна` : 'Эта функция доступна'} в подписке {tierLabel}
      </p>
      
      {userTier && userTier !== 'free' && (
        <p className="text-sm text-gray-500 mb-4">
          Ваш текущий тариф: {userTier.charAt(0).toUpperCase() + userTier.slice(1)}
        </p>
      )}
      
      {daysLeft !== undefined && daysLeft > 0 && daysLeft <= 7 && (
        <p className="text-sm text-yellow-500 mb-4">
          ⚠️ До окончания подписки осталось {daysLeft} {daysLeft === 1 ? 'день' : daysLessThan5 ? 'дня' : 'дней'}
        </p>
      )}

      <a
        href="/subscription"
        className="inline-block px-8 py-3 rounded-lg font-semibold text-[#0a0a1a] transition-all hover:scale-105"
        style={{
          background: `linear-gradient(135deg, ${tierColor} 0%, ${tierColor}cc 100%)`,
        }}
      >
        Оформить подписку →
      </a>
    </div>
  );
};

// Fix для склонения
const daysLessThan5 = (d: number) => d >= 2 && d <= 4;

export default AccessGuard;
